import { cellEditorBase, TimeCellEditorRef, RowNetworkStatus } from "@iventis/data-table";
import { customMediaQueryMax, customMediaQueryMin, screenSizeBreakpoints, styled } from "@iventis/styles";
import { Popover } from "@mui/material";
import { Content } from "@iventis/translations/content/typed-content";
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Header5 } from "@iventis/styles/src/components/texts";
import { formFieldVerticalGap } from "@iventis/styles/src/atomic-rules";
import { TimeSpan } from "@iventis/types";
import { add, compareAsc } from "date-fns";
import { TimeRelativeTo } from "@iventis/domain-model/model/timeRelativeTo";
import { inputCheckboxCSS } from "@iventis/styles/src/components/checkbox-elements";
import { addTimeSpanToDate, hasObjectArrayChanged, ZERO_TIME_SPAN, isValidDate } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stage, FormWizardTitle } from "@iventis/components";
import { EventBlockAndRelatedEventBlock, RunSheetData, RunSheetRowNode } from "./run-sheet.types";
import { TimeRulesCellEditorParams, TimeType } from "./time-rules.types";
import { EventBlockTimeProperties, SetFieldValue } from "./schedule.types";
import { TimeTypeDelegator } from "./time-type-delegator";
import { getDisplayTime } from "./run-sheet-helpers";
import { hasRelativeRuleChanged, getTimeTypeDelegatorInputPropsFromEventBlockType, addRelatedEventBlocksToArray, findEventBlocksRelatingToIdUsingMap } from "./time-rules.helpers";
import { isCalendarEventBlock } from "./event-block-helpers";

export const TimeRulesCellEditor = forwardRef<TimeCellEditorRef, TimeRulesCellEditorParams<RunSheetData>>(
    ({ node, eGridCell, context, getTimeCellValue, required, ...props }, ref) => {
        const translate = useIventisTranslate();
        const { relativeEventRules: oldRelativeEventRules, recurringEventRules: oldRecurringEventRules, estimatedTime: oldEstimatedTime, startAt, type, sessionId } = node.data;

        const { timezone } = context;

        const [values, setValues] = useState<EventBlockTimeProperties>({
            relativeEventRules: oldRelativeEventRules,
            recurringEventRules: oldRecurringEventRules,
            timeType: oldRelativeEventRules?.length > 0 ? TimeType.Relative : TimeType.Fixed,
            startTime: getDisplayTime(startAt?.value, timezone),
            estimatedTime: oldEstimatedTime,
        } as EventBlockTimeProperties);

        const potentialEventBlocksToRelateTo = useMemo(() => {
            const options = [];
            const eventBlockId = node.data.id;

            const relatingRulesArray: EventBlockAndRelatedEventBlock[] = [];
            props.api.forEachNode((node: RunSheetRowNode) => {
                addRelatedEventBlocksToArray(node, relatingRulesArray);
            });
            const eventBlocksRelatingToCurrentNode = findEventBlocksRelatingToIdUsingMap(relatingRulesArray, eventBlockId);

            props.api.forEachNode((node: RunSheetRowNode) => {
                // Filter itself from event blocks
                // Don't show the option if the option is the current event block, or the option relates to the current event block in some way, thus preventing circular dependencies
                if (
                    !(node.data?.id === eventBlockId || eventBlocksRelatingToCurrentNode.includes(node.data?.id)) &&
                    (!isCalendarEventBlock(type) || node.data.sessionId === sessionId) // If it is a calendar event block, it must share the same session id as it's relative
                ) {
                    options.push({ id: node.data?.id, name: node.data?.activity, type: node.data?.type, startAt: node.data?.startAt?.value, endAt: node.data?.endAt.value });
                }
            });
            return options;
        }, []);

        const setFieldValue: SetFieldValue<EventBlockTimeProperties> = (key, value) => {
            setValues({ ...values, [key]: value });
        };

        const newRelativeEventRules = useMemo(
            () => values.relativeEventRules?.map((rule) => ({ ...rule, duration: node.data.relativeEventRules?.find((r) => r.id === rule.id)?.duration || ZERO_TIME_SPAN })),
            [values]
        );

        useImperativeHandle(ref, () => ({
            getValue() {
                if (values.timeType === TimeType.Fixed) {
                    if (oldRelativeEventRules?.length > 0) {
                        props.api.getRowNode(node.id).setDataValue("relativeEventRules", []);
                        const rowsToDelete: RunSheetData[] = [];
                        props.api.forEachNode((n) => {
                            if (n.data.id === node.data.id && node.id !== n.id) {
                                // Since it's fixed, we remove all its' recurrences (but NOT own occurrence)
                                rowsToDelete.push(n.data);
                            }
                        });
                        props.api.applyTransaction({ remove: rowsToDelete });
                    }
                    const timeSplit = values.startTime?.split(":");
                    const timeCellValue = timeSplit != null ? getTimeCellValue(timeSplit[0], timeSplit[1], timeSplit[2], node, timezone) : null;
                    // if start time is later than end time then set end time to start time
                    if (compareAsc(timeCellValue?.value, node.data.endAt?.value) > 0) {
                        node.setDataValue("endAt", timeCellValue);
                    }
                    return timeCellValue;
                }

                // Must apply these changes first, so we can block identical network requests
                props.api.forEachNode((n) => {
                    if (n.data.id === node.data.id) {
                        // Set the new rules
                        n.setDataValue("relativeEventRules", newRelativeEventRules);
                        n.setDataValue("estimatedTime", values.estimatedTime);
                    }
                });

                let newStartAt = startAt;

                // Only update start time of rows if we have to (if there was no rule previously, or relatioships change, the run sheet will refresh after the rule change anyway)
                const continueUpdating = oldRelativeEventRules?.length > 0 || oldRelativeEventRules?.[0]?.relationship !== newRelativeEventRules?.[0]?.relationship;
                if (continueUpdating) {
                    props.api.forEachNode((instanceOfNode) => {
                        // Loop through and find each instance of the node
                        if (instanceOfNode.data.id === node.data.id) {
                            // Calculate new start time for each instance of the node
                            let newStartAtValue;
                            const newOffset = newRelativeEventRules?.[0]?.offset;
                            props.api.forEachNode((linkedNode: RunSheetRowNode) => {
                                // Loop through and find the node the instance is linked to
                                if (instanceOfNode.data.linkedEventId === linkedNode.data.id) {
                                    switch (newRelativeEventRules?.[0]?.relativeTo) {
                                        case TimeRelativeTo.StartTime:
                                            newStartAtValue = addTimeSpanToDate(newOffset, linkedNode.data.startAt?.value);
                                            break;
                                        case TimeRelativeTo.EndTime:
                                            newStartAtValue = addTimeSpanToDate(newOffset, linkedNode.data.endAt?.value);
                                            break;
                                        case TimeRelativeTo.ZeroTime: {
                                            const linkedNodeZeroAtTime =
                                                linkedNode.data?.zeroAt != null
                                                    ? addTimeSpanToDate(linkedNode.data.zeroAt as TimeSpan, linkedNode.data.startAt?.value)
                                                    : linkedNode.data.startAt?.value;
                                            newStartAtValue = addTimeSpanToDate(newOffset, linkedNodeZeroAtTime);
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                }
                            });
                            const startAt = { displayText: getDisplayTime(newStartAtValue, timezone), value: newStartAtValue };
                            instanceOfNode.setDataValue("startAt", startAt);

                            // Calculate new end time
                            const duration = newRelativeEventRules?.[0]?.duration;
                            if (duration != null) {
                                const [hours, minutes, seconds] = duration.split(":").map((t) => Number(t));
                                const newEndTime = add(startAt.value, { hours, minutes, seconds });
                                const endAt = { displayText: getDisplayTime(newEndTime, timezone), value: newEndTime };
                                instanceOfNode.setDataValue("endAt", endAt);
                            }

                            if (node.id === instanceOfNode.id) {
                                newStartAt = startAt;
                            }
                        }
                    });
                }
                return newStartAt;
            },
            isCancelBeforeStart() {
                // If the field is not required or has a value, while we are pending creation, make this cell uneditable
                const blockEditsUntilCreatedOnServer = (!required || isValidDate(startAt?.value)) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
                return blockEditsUntilCreatedOnServer;
            },
            isCancelAfterEnd() {
                switch (values.timeType) {
                    case TimeType.Relative: {
                        return !hasObjectArrayChanged(newRelativeEventRules, oldRelativeEventRules, hasRelativeRuleChanged);
                    }
                    case TimeType.Fixed: {
                        const [hours, minutes, seconds] = values.startTime?.split(":").map((t) => Number(t));
                        const newStartAt = getTimeCellValue(hours, minutes, seconds, node, timezone);
                        return compareAsc(newStartAt?.value, startAt?.value) === 0 && !(node.data.relativeEventRules?.length > 0);
                    }
                    default:
                        return false;
                }
            },
            isPopup() {
                return true;
            },
            getPopupPosition() {
                return "over";
            },
        }));

        const stage: Stage = {
            primaryButtonText: translate(Content.common.buttons.save_changes),
            primaryButtonCallback: () => props.api.stopEditing(),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: () => props.api.stopEditing(true) }],
            isValid: true,
        };

        return (
            <Popover
                open
                anchorEl={eGridCell}
                onClose={() => props.api.stopEditing(true)}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <TimeRulesEditorContainer>
                    <FormWizardTemplate
                        stages={[stage]}
                        currentStage={0}
                        isSubmitting={false}
                        isDeleting={false}
                        title={<StyledWizardTitle title={<Header5>{translate(Content.timerules.activity_start_time)}</Header5>} />}
                        className="time-rules-editor"
                    >
                        <TimeTypeDelegator
                            values={values}
                            setFieldValue={setFieldValue}
                            eventBlockTypes={props.eventBlockTypes}
                            currentEventBlockType={type}
                            scheduleDaysMap={props.scheduleDaysMap}
                            currentDate={startAt.value}
                            potentialEventBlocksToRelateTo={potentialEventBlocksToRelateTo}
                            className="time-type-delegator"
                            timezone={timezone}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...getTimeTypeDelegatorInputPropsFromEventBlockType(type, { hiddenInputs: { duration: () => true } })}
                        />
                    </FormWizardTemplate>
                </TimeRulesEditorContainer>
            </Popover>
        );
    }
);

const StyledWizardTitle = styled(FormWizardTitle)`
    padding: 19px 0 10px 20px;
`;

const TimeRulesEditorContainer = styled.div`
    display: flex;
    flex-direction: column;

    ${cellEditorBase}

    .time-rules-editor {
        ${customMediaQueryMax(screenSizeBreakpoints.medium)} {
            width: 376px;
        }

        ${customMediaQueryMin(screenSizeBreakpoints.medium)} {
            width: 624px;
        }
    }

    .time-type-delegator {
        display: flex;
        flex-direction: column;
        gap: ${formFieldVerticalGap};
    }

    .checkbox-input {
        ${inputCheckboxCSS}
    }
`;
